import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, MapPin, Phone, Search, Clock, ArrowUpRight } from 'lucide-react';
import { api } from '../lib/api';
import { withUtm } from '../lib/utm';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Reveal } from '../components/Reveal';
import { MonogramPattern } from '../components/Brush';

const PAGE = 18;

/** Rilevanza: città e CAP prima di provincia, nome, regione, indirizzo.
 *  Cercare «Roma» non deve restituire l'Emilia-Romagna in cima. */
const rankStore = (s, n) => {
  const city = s.city.toLowerCase();
  const province = s.province.toLowerCase();
  const region = s.region.toLowerCase();
  if (city === n) return 0;
  if (city.startsWith(n)) return 1;
  if (s.zip.startsWith(n)) return 2;
  if (city.includes(n)) return 3;
  if (province.startsWith(n)) return 4;
  if (s.name.toLowerCase().includes(n)) return 5;
  if (province.includes(n)) return 6;
  if (region.startsWith(n)) return 7;
  if (s.address.toLowerCase().includes(n)) return 8;
  if (region.includes(n)) return 9;
  return -1;
};

export default function WhereToBuy() {
  const { t, lang } = useLang();
  const [stores, setStores] = useState([]);
  const [channels, setChannels] = useState([]);
  const [regions, setRegions] = useState([]);
  const [totalAll, setTotalAll] = useState(0);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/stores?lang=${lang}`).then((r) => {
      setStores(r.data.items);
      setChannels(r.data.channels);
      setRegions(r.data.regions);
      setTotalAll(r.data.total_all);
      setSelected(r.data.items[0] || null);
    });
  }, [lang]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const base = stores.filter((s) => !region || s.region === region);
    if (!n) return base;
    return base
      .map((s) => ({ s, r: rankStore(s, n) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r || a.s.city.localeCompare(b.s.city))
      .map((x) => x.s);
  }, [stores, q, region]);

  useEffect(() => {
    setShown(PAGE);
    if (filtered.length > 0) setSelected(filtered[0]);
  }, [filtered]);

  return (
    <>
      <Seo title={t('where.title')} description={t('where.lead')} path="/dove-comprare" />

      <header className="py-14 md:py-24">
        <div className="wrap">
          <h1 className="display-xl max-w-[16ch]">{t('where.title')}</h1>
          <p className="prosa mt-8 border-t-2 border-inchiostro pt-6 text-lg">{t('where.lead')}</p>
        </div>
      </header>

      {/* tre canali: negozi, online, wholesale */}
      <section
        data-testid="channels"
        className="relative overflow-hidden bg-inchiostro text-carta"
      >
        <MonogramPattern className="absolute inset-0" opacity={0.05} />
        <div className="wrap relative py-12 md:py-16">
          <p className="accento mb-9 text-2xl !text-ocra">{t('where.channelsTitle')}</p>
          <div className="grid gap-10 md:grid-cols-3 md:gap-6">
            {channels.map((ch) => (
              <a
                key={ch.slug}
                href={withUtm(ch.url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`channel-${ch.slug}`}
                className="group flex flex-col justify-between bg-inchiostro p-6 transition-colors hover:bg-black md:p-9"
              >
                <div>
                  <h2 className="font-display text-3xl">{ch.name}</h2>
                  <p className="mt-2 text-[14px] font-semibold text-ocra">{ch.label}</p>
                  <p className="mt-3 text-base leading-relaxed text-carta/65">{ch.desc}</p>
                </div>
                <span className="mt-7 inline-flex items-center gap-2 text-[14px] font-bold text-carta">
                  {ch.cta}
                  <ArrowUpRight
                    size={14}
                    strokeWidth={2}
                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </span>
              </a>
            ))}
          </div>
          <p className="mt-8 max-w-[62ch] text-base leading-relaxed text-carta/70">
            {t('where.b2bNote')}{' '}
            <Link to="/professionisti/rivenditori" className="text-ocra link-ul">
              {t('common.becomeReseller')} →
            </Link>
          </p>
        </div>
      </section>

      {/* store locator */}
      <section data-testid="store-locator" className="py-14 md:py-20">
        <div className="wrap">
          <div className="mb-9 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="text-3xl md:text-4xl" data-testid="store-count">
                {t('where.storesCount')(filtered.length, totalAll)}
              </h2>
            </div>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="flex w-full items-end gap-3 md:w-auto"
            >
              <label className="flex-1 md:w-80">
                <span className="label">{t('where.searchPlaceholder')}</span>
                <input
                  autoComplete="off"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('where.searchPlaceholder')}
                  data-testid="store-search-input"
                  className="field"
                />
              </label>
              <button
                type="submit"
                data-testid="store-search-btn"
                aria-label={t('common.search')}
                className="btn btn-ink"
              >
                <Search size={15} strokeWidth={2} />
              </button>
            </form>
          </div>

          <div className="mb-10 flex flex-wrap gap-2 py-6">
            <button
              type="button"
              onClick={() => setRegion('')}
              data-testid="region-all"
              className={`px-3 py-1.5 text-[14px] font-semibold transition-colors ${
                region === ''
                  ? 'bg-marsala text-carta'
                  : 'border border-inchiostro/16 hover:bg-inchiostro hover:text-carta'
              }`}
            >
              {t('where.allRegions')}
            </button>
            {regions.map(([name, n]) => (
              <button
                key={name}
                type="button"
                onClick={() => setRegion(region === name ? '' : name)}
                data-testid={`region-${name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-1.5 text-[14px] font-semibold transition-colors ${
                  region === name
                    ? 'bg-marsala text-carta'
                    : 'border border-inchiostro/16 hover:bg-inchiostro hover:text-carta'
                }`}
              >
                {name} <span className="opacity-45">{n}</span>
              </button>
            ))}
          </div>

          <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-14">
            <div data-testid="store-list" className="min-w-0">
              {filtered.length === 0 && (
                <div className="py-14">
                  <p className="font-display text-2xl text-inchiostro/55">{t('where.noResults')}</p>
                  <Link to="/contatti" className="btn btn-line mt-7">
                    {t('nav.contact')}
                  </Link>
                </div>
              )}
              {filtered.slice(0, shown).map((s, i) => (
                <Reveal key={`${s.name}-${i}`} delay={Math.min(i, 6) * 0.03} y={12}>
                  <button
                    type="button"
                    onClick={() => setSelected(s)}
                    data-testid={`store-item-${i}`}
                    className={`w-full px-1 py-5 text-left transition-colors ${
                      selected?.name === s.name ? 'bg-carta-2' : 'hover:bg-carta-2'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="font-display text-xl md:text-2xl">{s.name}</h3>
                        <p className="mt-1 flex items-start gap-2 text-base text-inchiostro/70">
                          <MapPin size={13} className="mt-1 shrink-0 text-marsala" />
                          {s.address}
                        </p>
                        <p className="mt-1 flex items-start gap-2 text-base text-inchiostro/70">
                          <Clock size={13} className="mt-1 shrink-0 text-marsala" />
                          {s.hours}
                        </p>
                        <p className="mt-1 flex items-center gap-2 text-base text-inchiostro/70">
                          <Phone size={13} className="shrink-0 text-marsala" />
                          {s.phone}
                        </p>
                      </div>
                      <span className="shrink-0 text-[14px] font-bold text-inchiostro/70">
                        {s.region}
                      </span>
                    </div>
                  </button>
                </Reveal>
              ))}
              {shown < filtered.length && (
                <button
                  type="button"
                  onClick={() => setShown((n) => n + PAGE)}
                  data-testid="store-show-more"
                  className="btn btn-line mt-8"
                >
                  {t('where.showMore')} ({filtered.length - shown})
                </button>
              )}
            </div>

            <div className="min-w-0 lg:sticky lg:top-28 lg:self-start">
              {selected && (
                <div
                  data-testid="store-selected"
                  className="bg-carta-2 p-6 md:p-8"
                >
                  <h3 className="font-display text-2xl md:text-3xl">{selected.name}</h3>
                  <p className="mt-2 text-[14px] text-inchiostro/70">
                    {selected.province} · {selected.region}
                  </p>
                  <p className="mt-3 flex items-start gap-2 text-base text-inchiostro/75">
                    <MapPin size={13} className="mt-1 shrink-0 text-marsala" />
                    {selected.address}
                  </p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      selected.maps_query
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-in-maps"
                    className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-[14px] font-bold text-marsala-dark link-ul"
                  >
                    {t('where.openMaps')}
                    <ExternalLink size={12} strokeWidth={2} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
